import config from '@/config';
import { ApiBase, InternationalizedField, ListOptions } from '@/api/common';
import ApiList from '@/api/list';
import { Mutation } from '@/store';
import store from '@/store';
import { bus } from '@/bus';
import { BusEvent } from '@/models/bus';
import Video from '@/api/video';
import VideosI18n from '@/api/videos-i18n';
import InterfaceElement from '@/api/interfaceElement';

const NO_UPLOAD_FIELDS = [
  'SLUG',
  'icon_link',
  'bg_image_link',
  'banner_image_link',
  'cert_bg_url',
  'cert_detail_bg_url',
  'cert_sharing_bg_url',
  'created',
  'updated',
  'topic_id',
  'video_objects'
];

export default class Topic extends ApiBase<Topic> {
  public static readonly SLUG: string = 'topic';
  public readonly SLUG: string = 'topic';
  private static readonly URL_BASE: string = config.API_ROOT + '/topic/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public icon?: number | null = undefined;
  public icon_link?: string | null = undefined;
  public bg_image?: number | null = undefined;
  public bg_image_link?: string | null = undefined;
  public banner_image?: number | null = undefined;
  public banner_image_link?: string | null = undefined;
  public created?: string = undefined;
  public updated?: string = undefined;
  public topic_id?: string = undefined;
  public order?: number = undefined;
  public new?: boolean = undefined;
  public school?: number = undefined;
  public name: InternationalizedField<string> = new InternationalizedField<string>();
  public description: InternationalizedField<string> = new InternationalizedField<string>();
  public categories: number[] = undefined;
  public tags: string[] = [''];
  public user_interface_elements?: InterfaceElement[] = [];
  public prefix: string = undefined;
  public book_id: string = undefined;
  public publisher_id: string = undefined;
  public is_locked: boolean = undefined;
  public ios_sale_package: string = undefined;
  public android_sale_package: string = undefined;

  public video_objects: Video[] = [];
  public videos?: number[] = undefined;
  public videos_i18n: number[] = undefined;
  public videos_i18n_objects: InternationalizedField<
    VideosI18n[]
  > = new InternationalizedField<VideosI18n[]>();
  public listed?: boolean = undefined;
  public parent_project?: any = undefined;

  // currently unused
  public spreadsheet?: string = undefined;

  // New field for Catalog Service
  public cert_name_i18n: InternationalizedField<string> = new InternationalizedField<string>();
  public cert_bg?: number | null = undefined;
  public cert_bg_url?: string | null = undefined;
  public cert_detail_bg?: number | null = undefined;
  public cert_detail_bg_url?: string | null = undefined;
  public cert_sharing_bg?: number | null = undefined;
  public cert_sharing_bg_url?: string | null = undefined;

  public constructor() {
    super({
      urlBase: Topic.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });
  }

  public assign(response: any): Topic {
    super.assign(response);

    if (response) {
      if ('name' in response) {
        this.name = new InternationalizedField<string>(response.name);
      }
      if ('description' in response) {
        this.description = new InternationalizedField<string>(
          response.description
        );
      }
      if ('videos' in response) {
        this.videos = undefined;
        response.videos.forEach((video: any) => {
          this.video_objects.push(new Video().assign(video));
        });
      }
      if ('tags' in response) {
        this.tags = [];
        response.tags.forEach((tag: any) => {
          this.tags.push(tag);
        });
      }
      if ('user_interface_elements' in response) {
        this.user_interface_elements = [];
        response.user_interface_elements.forEach((element: any) => {
          this.user_interface_elements.push(element);
        });
      }
      if ('cert_name_i18n' in response) {
        this.cert_name_i18n = new InternationalizedField<string>(response.cert_name_i18n);
      }
    }

    return this;
  }

  public static async get(id: number): Promise<Topic> {
    return this.doGet({
      urlBase: Topic.URL_BASE,
      id,
      create: (response) => new Topic().assign(response)
    });
  }

  public static async list(): Promise<ApiList<Topic>> {
    const options: ListOptions<Topic> = {
      urlBase: Topic.URL_BASE,
      create: (response) => new Topic().assign(response)
    };
    return this.doList(options);
  }

  public static async listAll(): Promise<ApiList<Topic>> {
    return this.doList({
      urlBase: Topic.URL_BASE,
      create: (response) => new Topic().assign(response),
      all: true,
      parameters: {
        minimal: true
      },
      readFromCache: () => store.state.topicList,
      writeToCache: async (list: ApiList<Topic>) =>
        await store.commit(Mutation.CACHE_TOPIC_LIST, list)
    });
  }

  public async delete(): Promise<void> {
    const id = this.id;
    await super.delete();

    // In case the entity is cached
    await store.commit(Mutation.DELETE_FROM_TOPIC_LIST, id);
    bus.$emit(BusEvent.CACHE_CHANGED);
  }

  public async save(): Promise<number> {
    const isNew = !this.id;
    const id = await super.save();
    await this.saveInterfaceElements();
    await this.saveTags();

    if (isNew) {
      await store.commit(Mutation.ADD_TO_TOPIC_LIST, this);
    } else {
      await this.saveVideos();
      await store.commit(Mutation.TOPIC_UPDATED, this);
    }

    await super.save();
    bus.$emit(BusEvent.CACHE_CHANGED);

    return id;
  }

  public getName(): string {
    const name = this.name.getDefaultValue();
    if (name) {
      return name;
    }
    return '';
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }

  public async saveVideos() {
    for (const video of this.video_objects) {
      if (video.state === 'updated') {
        video.parent_id = this.id;
        video.parent_model = 'topic';
        if (!this.videos) {
          this.videos = [];
        }
        this.videos.push(await video.save());
      } else if (video.state === 'deleted' && video.id) {
        await video.delete();
      }
    }
  }

  public async saveInterfaceElements() {
    if (
      this.user_interface_elements &&
      this.user_interface_elements.length === 0
    ) {
      if (!this.id) {
        return delete this.user_interface_elements;
      } else {
        this.user_interface_elements = [];
      }
    }
  }

  public saveTags() {
    if (this.tags && this.tags.length === 0) {
      if (!this.id) {
        return delete this.tags;
      } else {
        this.tags = [];
      }
    }
  }
}
